import React, { useState, useEffect, useRef, type FormEvent } from 'react';
import { MapPin, X, Building2, CheckCircle2, RefreshCw } from 'lucide-react';
import type { Estate } from '../../types';
import AddressAutocomplete from '../AddressAutocomplete';
import { lookupAddressCoordinates } from '../../lib/addressHelper';
import { useDialogA11y } from '../../lib/useDialogA11y';

export interface EstateModalProps {
  isOpen: boolean;
  initialEstate?: Estate | 'new' | null;
  onClose: () => void;
  onSave: (data: {
    name: string;
    address: string;
    center: [number, number];
  }) => Promise<void> | void;
  allowMutation?: boolean;
}

export default function EstateModal({
  isOpen,
  initialEstate,
  onClose,
  onSave,
  allowMutation = true,
}: EstateModalProps) {
  const isNew = !initialEstate || initialEstate === 'new';
  const estate = isNew ? null : (initialEstate as Estate);

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [selectedCenter, setSelectedCenter] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName(estate?.name || '');
      setAddress(estate?.address || '');
      setSelectedCenter(estate?.center || null);
      setError('');
      setLoading(false);
      setTimeout(() => nameInputRef.current?.focus(), 60);
    }
  }, [isOpen, initialEstate]);

  const { ref: dialogRef } = useDialogA11y({ open: isOpen, onClose, closeOnEscape: !loading });

  if (!isOpen) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const cleanName = name.trim();
    const cleanAddress = address.trim();
    if (!cleanName) {
      setError('Angiv venligst et navn til reviret.');
      return;
    }
    if (!allowMutation) {
      setError('Du har ikke rettigheder til at ændre dette revir.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let center: [number, number] = selectedCenter || estate?.center || [11.96, 55.33];

      if (!selectedCenter && cleanAddress) {
        try {
          const coords = await lookupAddressCoordinates(cleanAddress);
          if (coords?.center) {
            center = coords.center;
          }
        } catch {
          // ignore coordinate lookup error, fallback to default
        }
      }

      await onSave({
        name: cleanName,
        address: cleanAddress,
        center,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fejl ved gemning af revir.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (!loading) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 'calc(var(--z-modal) + 50)',
        display: 'flex',
        padding: '16px',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        boxSizing: 'border-box',
      }}
    >
      <div
        ref={dialogRef}
        className="modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="estate-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#ffffff',
          borderRadius: 12,
          boxShadow: '0 20px 40px -8px rgba(0, 0, 0, 0.25)',
          width: '100%',
          maxWidth: 480,
          margin: 'auto',
          padding: 'clamp(20px, 4vw, 28px)',
          boxSizing: 'border-box',
          border: '1px solid #dce5d5',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 20,
            borderBottom: '1px solid #edf2e8',
            paddingBottom: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: '#eef6ec',
                color: '#2d6a4f',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Building2 size={20} />
            </div>
            <div>
              <h2
                id="estate-modal-title"
                style={{ margin: 0, fontSize: 18, color: '#1a2b21', fontWeight: 700 }}
              >
                {isNew ? 'Opret nyt revir' : 'Rediger revir'}
              </h2>
              <p className="muted" style={{ margin: '2px 0 0', fontSize: 12 }}>
                {isNew
                  ? 'Angiv revirets navn og adresse for at placere det på kortet.'
                  : 'Opdater oplysninger for dit revir.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            disabled={loading}
            aria-label="Luk dialog"
            style={{ minHeight: 36, minWidth: 36 }}
          >
            <X size={18} />
          </button>
        </div>

        {error && (
          <div
            className="error-banner"
            role="alert"
            style={{ marginBottom: 16, padding: '10px 14px', fontSize: 13 }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <label
            htmlFor="estate-name-input"
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: '#263f2b' }}>Revirets navn</span>
            <input
              id="estate-name-input"
              aria-label="Revirets navn"
              ref={nameInputRef}
              type="text"
              required
              maxLength={120}
              disabled={loading}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Fx Bavnehøj Jagtrevir eller Hovedgården"
              className="form-input"
              style={{ minHeight: 44, fontSize: 14 }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#263f2b' }}>
              Adresse eller postnummer
            </span>
            <AddressAutocomplete
              name="address"
              value={address}
              onChange={(text) => {
                setAddress(text);
                setSelectedCenter(null);
              }}
              placeholder="Søg adresse (f.eks. Bregentvedvej 1, Haslev)"
              onSelectAddress={(_addr, center) => setSelectedCenter(center)}
            />
          </label>

          {selectedCenter && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: 6,
                fontSize: 12,
                color: '#166534',
              }}
            >
              <CheckCircle2 size={15} style={{ flexShrink: 0 }} />
              <span>Adressen er verificeret og placeres automatisk på kortet.</span>
            </div>
          )}

          <p className="muted" style={{ margin: 0, fontSize: 12, lineHeight: 1.4 }}>
            Ved at angive en adresse centreres kortet direkte på ejendommens beliggenhed med
            forårs-ortofoto og matrikelskel.
          </p>

          <div
            style={{
              display: 'flex',
              gap: 10,
              justifyContent: 'flex-end',
              marginTop: 10,
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              className="button secondary"
              onClick={onClose}
              disabled={loading}
              style={{ minHeight: 44, padding: '10px 18px', fontSize: 14 }}
            >
              Annuller
            </button>
            <button
              type="submit"
              className="button primary"
              disabled={loading || !name.trim()}
              aria-label={isNew ? 'Gem revir' : 'Gem ændringer'}
              style={{
                minHeight: 44,
                padding: '10px 22px',
                fontSize: 14,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
              }}
            >
              {loading ? (
                <>
                  <RefreshCw size={15} className="spin" />
                  <span>Gemmer revir…</span>
                </>
              ) : (
                <>
                  <MapPin size={16} />
                  <span>{isNew ? 'Gem revir' : 'Gem ændringer'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
